/**
 * Google sign-in for the Capacitor native shells (iOS + Android).
 *
 * WHY THIS EXISTS
 * ---------------
 * Amplify's `signInWithRedirect()` drives the Cognito Hosted UI by navigating the
 * *current* window. Inside a Capacitor WebView that means Google's consent screen
 * would load in an embedded WebView, which Google refuses outright with
 * `disallowed_useragent`. So native gets its own flow: the OAuth leg runs in the
 * real system browser and the result is handed back through a custom URL scheme.
 *
 *   [app]    tap the link (a plain <a target="_blank">; Capacitor's
 *            WebViewDelegationHandler routes it to UIApplication.shared.open →
 *            system Safari, which Google accepts)
 *   [safari] Cognito Hosted UI → Google → consent → Cognito
 *            302 → com.brokeornot.app://login?code=…&state=bon-native.<nonce>
 *   [app]    @capacitor/app's `appUrlOpen` fires → we exchange the code (PKCE) and
 *            write the tokens into Amplify's token store.
 *
 * Cognito redirects straight back into the app because `com.brokeornot.app://login`
 * is registered as a callback URL on the app client (see `oauthRedirects` in
 * packages/infra/bin/broke-or-not.ts) and as a URL scheme in the iOS Info.plist /
 * Android manifest. No web page is involved.
 *
 * THE BRIDGE FALLBACK
 * -------------------
 * Setting VITE_OAUTH_NATIVE_BRIDGE_ORIGIN switches the redirect back to
 * `<origin>/login` and relies on the inline script in apps/web/index.html to forward
 * `?code&state` on to the custom scheme. That exists for an environment where the
 * custom scheme is NOT in the Cognito client's callback list (Cognito matches
 * redirect_uri exactly, so an unregistered scheme fails with `redirect_mismatch`) —
 * it lets native sign-in work by bouncing off an already-whitelisted web origin
 * instead. Unset by default; the direct scheme is the normal path.
 *
 * The client is public (`generateSecret: false`), so the token exchange is plain
 * PKCE with no Authorization header. On native `fetch` is proxied through
 * CapacitorHttp (URLSession/OkHttp), so the POST is not subject to WebView CORS.
 */
import { decodeJWT } from 'aws-amplify/auth';
import { DefaultTokenStore } from 'aws-amplify/auth/cognito';
import { Amplify } from 'aws-amplify';
import { App } from '@capacitor/app';
import type { AnyRouter } from '@tanstack/react-router';
import { nativeTokenStorage } from './amplify';
import { isRunningInNativeShell } from './pwa';

/** Custom URL scheme registered in Info.plist / AndroidManifest.xml. */
const APP_SCHEME = 'com.brokeornot.app';

/** Deep-link path the bridge redirects to. */
export const NATIVE_CALLBACK_URL = `${APP_SCHEME}://login`;

/**
 * Marks an OAuth `state` as belonging to the native flow. Cognito echoes `state`
 * back to the callback verbatim, so the bridge script can recognise a native
 * round-trip and forward it instead of completing sign-in in the browser.
 * Kept in sync with the inline script in apps/web/index.html.
 */
export const NATIVE_STATE_PREFIX = 'bon-native.';

/** localStorage key holding the in-flight {state, verifier, redirectUri}. */
const PENDING_KEY = 'bon.nativeGoogleAuth.pending';

interface PendingAuth {
  state: string;
  verifier: string;
  redirectUri: string;
}

/**
 * Where Cognito should send the authorization code.
 *
 * Normally the app's own custom scheme, so the browser hands control straight back
 * with no intermediate page. Whatever this returns must be listed verbatim in the
 * Cognito client's callback URLs — Cognito matches redirect_uri exactly.
 *
 * VITE_OAUTH_NATIVE_BRIDGE_ORIGIN opts into the bridge fallback described in the
 * file header (for an environment where the scheme isn't registered).
 */
function resolveRedirectUri(): string {
  const bridgeOrigin = import.meta.env.VITE_OAUTH_NATIVE_BRIDGE_ORIGIN as string | undefined;
  if (bridgeOrigin) return `${bridgeOrigin.replace(/\/$/, '')}/login`;
  return NATIVE_CALLBACK_URL;
}

function oauthDomain(): string {
  return (
    (import.meta.env.VITE_OAUTH_DOMAIN as string | undefined) ?? 'auth.sandbox.app.brokeornot.com'
  );
}

function userPoolClientId(): string {
  return import.meta.env.VITE_USER_POOL_CLIENT_ID as string;
}

// ---------------------------------------------------------------------------
// PKCE
// ---------------------------------------------------------------------------

/** RFC 7636 base64url: standard base64 with -_ and no padding. */
function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomUrlSafeString(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function sha256Challenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64UrlEncode(new Uint8Array(digest));
}

// ---------------------------------------------------------------------------
// Step 1 — build the authorize URL
// ---------------------------------------------------------------------------

/**
 * Creates a fresh PKCE pair, persists it, and returns the Hosted-UI authorize URL
 * to point a link at.
 *
 * The URL is built ahead of the click (rather than in the click handler) so the
 * link can be a real `<a target="_blank">`: WKWebView only reliably hands a
 * navigation to the system browser when it originates directly from a user
 * gesture, and awaiting `crypto.subtle.digest` inside the handler would break that.
 */
export async function prepareNativeGoogleAuthUrl(): Promise<string> {
  const verifier = randomUrlSafeString(64);
  const challenge = await sha256Challenge(verifier);
  const state = `${NATIVE_STATE_PREFIX}${randomUrlSafeString(16)}`;
  const redirectUri = resolveRedirectUri();

  const pending: PendingAuth = { state, verifier, redirectUri };
  localStorage.setItem(PENDING_KEY, JSON.stringify(pending));

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: userPoolClientId(),
    redirect_uri: redirectUri,
    scope: 'openid email profile',
    identity_provider: 'Google',
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });

  return `https://${oauthDomain()}/oauth2/authorize?${params.toString()}`;
}

// ---------------------------------------------------------------------------
// Step 2 — handle the deep link back into the app
// ---------------------------------------------------------------------------

interface TokenResponse {
  access_token?: string;
  id_token?: string;
  refresh_token?: string;
  error?: string;
  error_description?: string;
}

async function exchangeCodeForTokens(code: string, pending: PendingAuth): Promise<TokenResponse> {
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: userPoolClientId(),
    code,
    redirect_uri: pending.redirectUri,
    code_verifier: pending.verifier,
  });

  const response = await fetch(`https://${oauthDomain()}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  const json = (await response.json()) as TokenResponse;
  if (!response.ok || json.error) {
    throw new Error(json.error_description ?? json.error ?? `Token exchange failed (${response.status})`);
  }
  return json;
}

/**
 * Writes the freshly minted tokens into Amplify's token store so the rest of the
 * app (fetchAuthSession, the _app route guard, api.ts) sees a normal signed-in
 * session.
 *
 * `DefaultTokenStore` is a public export of `aws-amplify/auth/cognito` and is
 * stateless — it only reads/writes the KeyValueStorage handed to it. Pointing a
 * second instance at the *same* adapter Amplify was configured with therefore
 * writes exactly the keys Amplify's own store would.
 *
 * NOTE: `setOAuthMetadata({oauthSignIn: true})` is deliberately NOT written.
 * It would make a later `signOut()` take Amplify's OAuth-aware path and try a
 * Hosted-UI logout redirect inside the WebView — the failure recorded in debug
 * session `google-sso-login-blank`. A local token clear is what we want here.
 */
async function storeTokens(tokens: TokenResponse): Promise<void> {
  if (!tokens.access_token) throw new Error('Cognito returned no access token');

  const authConfig = Amplify.getConfig().Auth;
  if (!authConfig) throw new Error('Amplify Auth is not configured');
  if (!nativeTokenStorage) throw new Error('Native token storage is unavailable');

  const accessToken = decodeJWT(tokens.access_token);
  const idToken = tokens.id_token ? decodeJWT(tokens.id_token) : undefined;

  // Same formula Amplify uses in cacheCognitoTokens: issued-at minus now, in ms.
  const issuedAtMillis = (accessToken.payload.iat ?? 0) * 1000;
  const clockDrift = issuedAtMillis > 0 ? issuedAtMillis - Date.now() : 0;

  // Amplify keys everything under the username; Cognito puts it on the id token
  // for federated users, and the access token carries it as `username`.
  const username =
    (idToken?.payload['cognito:username'] as string | undefined) ??
    (accessToken.payload.username as string | undefined) ??
    (accessToken.payload.sub as string);

  const store = new DefaultTokenStore();
  store.setAuthConfig(authConfig);
  store.setKeyValueStorage(nativeTokenStorage);

  await store.storeTokens({
    accessToken,
    idToken,
    refreshToken: tokens.refresh_token,
    clockDrift,
    username,
    signInDetails: {
      loginId: (idToken?.payload.email as string | undefined) ?? username,
      authFlowType: 'USER_AUTH',
    },
  });
}

/** Emitted so the login page can surface a failure instead of silently doing nothing. */
export const NATIVE_GOOGLE_AUTH_ERROR_EVENT = 'bon:native-google-auth-error';

function reportError(message: string): void {
  window.dispatchEvent(new CustomEvent(NATIVE_GOOGLE_AUTH_ERROR_EVENT, { detail: message }));
}

/**
 * Registers the `appUrlOpen` listener that completes the flow. Self-gates on
 * native, so an unconditional call from main.tsx is safe on web.
 */
export function registerNativeGoogleAuth(router: AnyRouter): void {
  if (!isRunningInNativeShell()) return;

  void App.addListener('appUrlOpen', (event) => {
    void handleAppUrlOpen(event.url, router);
  });
}

async function handleAppUrlOpen(rawUrl: string | undefined, router: AnyRouter): Promise<void> {
  if (!rawUrl?.startsWith(NATIVE_CALLBACK_URL)) return;

  // A custom-scheme URL parses fine with the URL constructor; the query lives on
  // `search` exactly as it would for https.
  const params = new URL(rawUrl).searchParams;
  const stored = localStorage.getItem(PENDING_KEY);
  localStorage.removeItem(PENDING_KEY);

  const oauthError = params.get('error');
  if (oauthError) {
    reportError(params.get('error_description') ?? oauthError);
    return;
  }

  const code = params.get('code');
  const state = params.get('state');
  if (!code || !state) {
    reportError('Sign-in response was incomplete. Please try again.');
    return;
  }

  if (!stored) {
    reportError('Sign-in session expired. Please try again.');
    return;
  }

  const pending = JSON.parse(stored) as PendingAuth;
  // CSRF guard: the state we get back must be the one we minted.
  if (pending.state !== state) {
    reportError('Sign-in could not be verified. Please try again.');
    return;
  }

  try {
    const tokens = await exchangeCodeForTokens(code, pending);
    await storeTokens(tokens);
    await router.navigate({ to: '/' });
  } catch (error) {
    reportError(error instanceof Error ? error.message : 'Google sign-in failed.');
  }
}
